import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../../../context/ThemeContext";
import { getData } from "../../../../services/Fetchnodeservices";

// ===================== ICONS =====================
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const FilterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <polygon points="22 3 2 3 10 13 10 21 14 18 14 13 22 3" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const BackIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const SparklesIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M12 3L14 8L19 10L14 12L12 17L10 12L5 10L10 8L12 3Z"/>
    <path d="M19 4L20 7L23 8L20 9L19 12L18 9L15 8L18 7L19 4Z"/>
  </svg>
);

const InvoiceIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <polyline points="10 9 9 9 8 9"/>
  </svg>
);

const RefreshIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M23 4v6h-6"/>
    <path d="M1 20v-6h6"/>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/>
    <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ===================== STATUS BADGE (Theme-Aware) =====================
const StatusBadge = ({ status, theme }) => {
  const getStatusConfig = (statusValue) => {
    const statusStr = String(statusValue || '').toLowerCase();
    const statusMap = {
      '1': { bg: `${theme.success}1a`, color: theme.success, label: 'Paid' },
      '0': { bg: `${theme.warning}1a`, color: theme.warning, label: 'Pending' },
      'paid': { bg: `${theme.success}1a`, color: theme.success, label: 'Paid' },
      'pending': { bg: `${theme.warning}1a`, color: theme.warning, label: 'Pending' },
      'overdue': { bg: `${theme.error || theme.danger}1a`, color: theme.error || theme.danger, label: 'Overdue' },
      'cancelled': { bg: `${theme.textMuted}1a`, color: theme.textMuted, label: 'Cancelled' },
    };
    return statusMap[statusStr] || statusMap['0'];
  };
  
  const c = getStatusConfig(status);
  
  return (
    <span style={{ 
      display: 'inline-flex', 
      alignItems: 'center', 
      gap: '6px', 
      padding: '4px 12px', 
      borderRadius: '20px', 
      fontSize: '11px', 
      fontWeight: '600', 
      background: c.bg, 
      color: c.color 
    }}>
      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: c.color }} />
      {c.label}
    </span>
  );
};

// ===================== STAT CARD (Theme-Aware) =====================
const StatCard = ({ icon, title, value, subtitle, color, theme }) => {
  return (
    <div style={{
      background: theme.surface,
      borderRadius: '20px',
      padding: '20px',
      border: `1px solid ${theme.border}`,
      transition: 'transform 0.2s ease, box-shadow 0.2s ease'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = `0 8px 25px ${theme.primary}20`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{
          width: '48px',
          height: '48px',
          background: `linear-gradient(135deg, ${color}20, ${color}10)`,
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: color
        }}>
          {icon}
        </div>
        {subtitle && (
          <span style={{
            fontSize: '11px',
            color: theme.textMuted,
            background: theme.surfaceLighter,
            padding: '4px 10px',
            borderRadius: '20px'
          }}>
            {subtitle}
          </span>
        )}
      </div>
      <div>
        <p style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '8px' }}>{title}</p>
        <p style={{ fontSize: '28px', fontWeight: '700', color: theme.textPrimary, margin: 0 }}>{value}</p>
      </div>
    </div>
  );
};

// ===================== INVOICES TABLE (Theme-Aware) =====================
const InvoicesTable = ({ invoices, loading, onView, theme }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const getNormalizedStatus = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === '1' || s === 'paid') return 'paid';
    if (s === '0' || s === 'pending') return 'pending';
    if (s === 'overdue') return 'overdue';
    if (s === 'cancelled') return 'cancelled';
    return 'pending';
  };

  const filteredInvoices = invoices.filter(inv => {
    const receiverName = inv.receiver_name || '';
    const invoiceNumber = String(inv.invoice_number || inv.id || '');
    const companyName = inv.company_name || '';
    const invStatus = getNormalizedStatus(inv.status);
    
    const matchesSearch = receiverName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          invoiceNumber.includes(searchTerm) ||
                          companyName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });
  
  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);
  const paginatedInvoices = filteredInvoices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: theme.textSecondary }}>
        <div style={{ 
          width: '48px', 
          height: '48px', 
          border: `3px solid ${theme.border}`, 
          borderTopColor: theme.primary, 
          borderRadius: '50%', 
          margin: '0 auto 16px auto', 
          animation: 'spin 1s linear infinite' 
        }} />
        <p>Loading invoices...</p>
      </div>
    );
  }

  return (
    <div style={{ 
      background: theme.surface, 
      borderRadius: '32px', 
      overflow: 'hidden', 
      boxShadow: `0 25px 50px -12px ${theme.primary}15`, 
      border: `1px solid ${theme.border}` 
    }}>
      {/* Header */}
      <div style={{ 
        padding: '20px 24px', 
        borderBottom: `1px solid ${theme.border}`, 
        background: theme.surfaceLighter,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            padding: '8px 16px', 
            border: `1px solid ${theme.border}`, 
            borderRadius: '40px', 
            backgroundColor: theme.surface 
          }}>
            <SearchIcon style={{ color: theme.textMuted, width: '14px', height: '14px' }} />
            <input
              type="text"
              placeholder="Search by receiver or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                background: 'transparent',
                border: 'none',
                outline: 'none',
                fontSize: '13px',
                color: theme.textPrimary,
                width: '220px'
              }}
            />
          </div>
          
          {/* Status Filter */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            padding: '8px 16px', 
            border: `1px solid ${theme.border}`, 
            borderRadius: '40px', 
            backgroundColor: theme.surface 
          }}>
            <FilterIcon style={{ color: theme.textMuted, width: '14px', height: '14px' }} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                background: 'transparent',
                border: 'none',
                outline: 'none',
                fontSize: '13px',
                color: theme.textPrimary,
                cursor: 'pointer'
              }}
            >
              <option value="all">All Status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
        
        <button 
          onClick={() => {
            setSearchTerm('');
            setStatusFilter('all');
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            borderRadius: '40px',
            border: `1px solid ${theme.border}`,
            background: 'transparent',
            color: theme.textSecondary,
            cursor: 'pointer',
            fontSize: '12px',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = theme.surfaceHover || theme.surfaceLighter}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        >
          <RefreshIcon /> Clear Filters
        </button>
      </div>

      {/* Table */}
      {filteredInvoices.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 40px', color: theme.textMuted }}>
          <div style={{ 
            width: '80px', 
            height: '80px', 
            background: theme.surfaceLighter, 
            borderRadius: '40px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            margin: '0 auto 20px auto' 
          }}>
            <InvoiceIcon style={{ width: '40px', height: '40px', opacity: 0.5 }} />
          </div>
          <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px', color: theme.textPrimary }}>No invoices found</p>
          <p style={{ fontSize: '13px' }}>No invoices match your search criteria</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: theme.surfaceLighter, borderBottom: `1px solid ${theme.border}` }}>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: theme.primary }}>Invoice #</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: theme.primary }}>Receiver</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: theme.primary }}>Company</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: theme.primary }}>Status</th>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: theme.primary }}>Date</th>
                <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: theme.primary }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedInvoices.map((invoice, idx) => {
                return (
                  <tr 
                    key={idx} 
                    style={{ 
                      borderBottom: `1px solid ${theme.border}`, 
                      transition: 'background 0.2s ease' 
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = theme.surfaceHover || `${theme.primary}08`}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '600', color: theme.primary, fontFamily: 'monospace' }}>
                      #{invoice.invoice_number || invoice.id}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: theme.textPrimary }}>
                      {invoice.receiver_name || '-'}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: theme.textSecondary }}>
                      {invoice.company_name || '-'}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <StatusBadge status={invoice.status} theme={theme} />
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '12px', color: theme.textMuted }}>
                      {invoice.created_at ? new Date(invoice.created_at).toLocaleString() : '-'}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button 
                          onClick={() => onView(invoice)} 
                          style={{
                            padding: '6px 12px',
                            borderRadius: '8px',
                            border: `1px solid ${theme.border}`,
                            background: 'transparent',
                            color: theme.textSecondary,
                            cursor: 'pointer',
                            fontSize: '11px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = theme.surfaceHover || theme.surfaceLighter}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <EyeIcon /> View
                        </button>
                        <button 
                          onClick={() => {
                            alert(`Download invoice #${invoice.invoice_number || invoice.id}`);
                          }} 
                          style={{
                            padding: '6px 12px',
                            borderRadius: '8px',
                            border: `1px solid ${theme.border}`,
                            background: 'transparent',
                            color: theme.primary,
                            cursor: 'pointer',
                            fontSize: '11px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = theme.surfaceHover || theme.surfaceLighter}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <DownloadIcon /> PDF
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ 
          padding: '14px 20px', 
          borderTop: `1px solid ${theme.border}`, 
          background: theme.surfaceLighter,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <span style={{ fontSize: '12px', color: theme.textSecondary }}>
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredInvoices.length)} of {filteredInvoices.length} entries
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '6px 12px',
                borderRadius: '40px',
                border: `1px solid ${theme.border}`,
                background: theme.surface,
                color: currentPage === 1 ? theme.textMuted : theme.textPrimary,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                transition: 'all 0.2s ease'
              }}
            >
              <ChevronLeftIcon /> Prev
            </button>
            <span style={{ padding: '6px 12px', fontSize: '12px', color: theme.textPrimary }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: '6px 12px',
                borderRadius: '40px',
                border: `1px solid ${theme.border}`,
                background: theme.surface,
                color: currentPage === totalPages ? theme.textMuted : theme.textPrimary,
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                transition: 'all 0.2s ease'
              }}
            >
              Next <ChevronRightIcon />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ===================== VIEW INVOICE MODAL (Theme-Aware) =====================
const ViewInvoiceModal = ({ invoice, onClose, theme }) => {
  if (!invoice) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.7)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: theme.surface,
        borderRadius: '24px',
        width: '100%',
        maxWidth: '500px',
        overflow: 'hidden',
        boxShadow: `0 25px 50px -12px ${theme.primary}30`,
        border: `1px solid ${theme.border}`
      }}>
        <div style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${theme.border}`,
          background: theme.surfaceLighter,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', color: theme.textPrimary, margin: 0 }}>Invoice Details</h3>
          <button 
            onClick={onClose} 
            style={{ 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer', 
              color: theme.textMuted, 
              fontSize: '22px',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = theme.error || theme.danger}
            onMouseLeave={(e) => e.currentTarget.style.color = theme.textMuted}
          >×</button>
        </div>
        
        <div style={{ padding: '24px' }}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '11px', color: theme.textMuted, display: 'block', marginBottom: '6px', fontWeight: '600' }}>Invoice Number</label>
            <p style={{ fontSize: '16px', fontWeight: '700', color: theme.primary, margin: 0, fontFamily: 'monospace' }}>
              #{invoice.invoice_number || invoice.id}
            </p>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '11px', color: theme.textMuted, display: 'block', marginBottom: '6px', fontWeight: '600' }}>Receiver Name</label>
            <p style={{ fontSize: '15px', color: theme.textPrimary, margin: 0 }}>{invoice.receiver_name || '-'}</p>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '11px', color: theme.textMuted, display: 'block', marginBottom: '6px', fontWeight: '600' }}>Company Name</label>
            <p style={{ fontSize: '15px', color: theme.textPrimary, margin: 0 }}>{invoice.company_name || '-'}</p>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '11px', color: theme.textMuted, display: 'block', marginBottom: '6px', fontWeight: '600' }}>Status</label>
            <StatusBadge status={invoice.status} theme={theme} />
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '11px', color: theme.textMuted, display: 'block', marginBottom: '6px', fontWeight: '600' }}>Created Date</label>
            <p style={{ fontSize: '14px', color: theme.textSecondary, margin: 0 }}>
              {invoice.created_at ? new Date(invoice.created_at).toLocaleString() : '-'}
            </p>
          </div>
          
          {invoice.due_date && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '11px', color: theme.textMuted, display: 'block', marginBottom: '6px', fontWeight: '600' }}>Due Date</label>
              <p style={{ fontSize: '14px', color: theme.textSecondary, margin: 0 }}>
                {new Date(invoice.due_date).toLocaleString()}
              </p>
            </div>
          )}
          
          {invoice.amount && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '11px', color: theme.textMuted, display: 'block', marginBottom: '6px', fontWeight: '600' }}>Amount</label>
              <p style={{ fontSize: '20px', fontWeight: '700', color: theme.success, margin: 0 }}>
                ₹{parseFloat(invoice.amount).toLocaleString()}
              </p>
            </div>
          )}
        </div>
        
        <div style={{
          padding: '16px 24px',
          borderTop: `1px solid ${theme.border}`,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button 
            onClick={() => {
              alert(`Download PDF for invoice #${invoice.invoice_number || invoice.id}`);
            }}
            style={{
              padding: '8px 20px',
              borderRadius: '40px',
              border: `1px solid ${theme.border}`,
              background: 'transparent',
              color: theme.primary,
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = theme.surfaceHover || theme.surfaceLighter}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <DownloadIcon /> Download PDF
          </button>
          <button 
            onClick={onClose} 
            style={{
              padding: '8px 20px',
              borderRadius: '40px',
              border: 'none',
              background: theme.primary,
              color: 'white',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              transition: 'transform 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >Close</button>
        </div>
      </div>
    </div>
  );
};

// ===================== MAIN COMPONENT (Theme-Aware) =====================
const Invoices = () => {
  const { theme } = useContext(ThemeContext); // ← Theme Context
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await getData("Payments/invoices");
      console.log("Invoices Response:", response);
      if (response && response.status === true && response.data) {
        setInvoices(response.data);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const handleBack = () => navigate(-1);

  const handleView = (invoice) => {
    setSelectedInvoice(invoice);
    setShowModal(true);
  };

  const getStatusForStats = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === '1' || s === 'paid') return 'paid';
    if (s === '0' || s === 'pending') return 'pending';
    if (s === 'overdue') return 'overdue';
    if (s === 'cancelled') return 'cancelled';
    return 'pending';
  };

  const stats = {
    total: invoices.length,
    pending: invoices.filter(inv => getStatusForStats(inv.status) === 'pending').length,
    paid: invoices.filter(inv => getStatusForStats(inv.status) === 'paid').length,
    overdue: invoices.filter(inv => getStatusForStats(inv.status) === 'overdue').length
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: theme.background, 
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", 
      transition: 'all 0.3s ease' 
    }}>
      <div style={{ position: 'relative', zIndex: 1, padding: '32px 24px' }}>
        {/* Header */}
        <div style={{ maxWidth: '1200px', margin: '0 auto 32px auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
            <button 
              onClick={handleBack} 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                padding: '10px 20px',
                background: theme.surfaceLighter,
                border: `1px solid ${theme.border}`,
                borderRadius: '40px',
                color: theme.textPrimary,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = theme.surfaceHover || theme.surface}
              onMouseLeave={(e) => e.currentTarget.style.background = theme.surfaceLighter}
            >
              <BackIcon /> Back to Dashboard
            </button>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px', 
                background: theme.surfaceLighter, 
                padding: '8px 20px', 
                borderRadius: '40px', 
                border: `1px solid ${theme.border}` 
              }}>
                <SparklesIcon />
                <span style={{ color: theme.textPrimary, fontSize: '14px', fontWeight: '500' }}>Invoices Management</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Stats Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
            <StatCard 
              icon={<InvoiceIcon />}
              title="Total Invoices"
              value={stats.total}
              color={theme.primary}
              theme={theme}
            />
            <StatCard 
              icon={<RefreshIcon />}
              title="Pending"
              value={stats.pending}
              color={theme.warning}
              theme={theme}
            />
            <StatCard 
              icon={<CheckIcon />}
              title="Paid"
              value={stats.paid}
              color={theme.success}
              theme={theme}
            />
            <StatCard 
              icon={<XIcon />}
              title="Overdue"
              value={stats.overdue}
              color={theme.error || theme.danger}
              theme={theme}
            />
          </div>

          {/* Invoices Table */}
          <InvoicesTable
            invoices={invoices}
            loading={loading}
            onView={handleView}
            theme={theme}
          />
        </div>
      </div>

      {/* View Invoice Modal */}
      {showModal && (
        <ViewInvoiceModal
          invoice={selectedInvoice}
          onClose={() => {
            setShowModal(false);
            setSelectedInvoice(null);
          }}
          theme={theme}
        />
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Invoices;